import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { X, Cookie, Settings, Shield, BarChart3, Megaphone, MapPin } from "lucide-react";

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always on
    analytics: true,
    functional: true,
    advertising: false,
  });

  useEffect(() => {
    // Check if user has already set preferences
    const savedPrefs = localStorage.getItem('delevr_cookie_prefs');
    if (!savedPrefs) {
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('delevr_cookie_prefs', JSON.stringify(prefs));
    localStorage.setItem('delevr_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowCustomize(false);
  };

  const acceptAll = () => {
    const allOn = { necessary: true, analytics: true, functional: true, advertising: true };
    setPreferences(allOn);
    savePreferences(allOn);
  };

  const rejectOptional = () => {
    const onlyNecessary = { necessary: true, analytics: false, functional: false, advertising: false };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-50" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
        <Card className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {!showCustomize ? (
            /* Main Banner */
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1E4DB7]/10 rounded-xl flex items-center justify-center shrink-0">
                  <Cookie className="w-6 h-6 text-[#1E4DB7]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Cookie Preferences</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    We use cookies to improve your experience, maintain security, analyze traffic, and personalize content. 
                    Manage your preferences anytime.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      data-testid="cookie-accept-all"
                      onClick={acceptAll}
                      className="bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full px-6"
                    >
                      Accept All
                    </Button>
                    <Button 
                      data-testid="cookie-reject-optional"
                      onClick={rejectOptional}
                      variant="outline"
                      className="rounded-full px-6 border-slate-300"
                    >
                      Reject Optional
                    </Button>
                    <Button 
                      data-testid="cookie-customize"
                      onClick={() => setShowCustomize(true)}
                      variant="ghost"
                      className="rounded-full px-6 text-[#1E4DB7]"
                    >
                      <Settings className="w-4 h-4 mr-2" /> Customize
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Customize Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900">Customize Cookie Preferences</h3>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowCustomize(false)}
                  className="rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Strictly Necessary */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1E4DB7] rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Strictly Necessary</p>
                      <p className="text-xs text-slate-500">Required for core functionality</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-green-600 font-medium">Always On</span>
                    <Switch checked={true} disabled className="data-[state=checked]:bg-green-500" />
                  </div>
                </div>

                {/* Performance & Analytics */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Performance & Analytics</p>
                      <p className="text-xs text-slate-500">Help us improve the platform</p>
                    </div>
                  </div>
                  <Switch 
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                    className="data-[state=checked]:bg-[#1E4DB7]"
                  />
                </div>

                {/* Functional */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Settings className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Functional Cookies</p>
                      <p className="text-xs text-slate-500">Remember your preferences</p>
                    </div>
                  </div>
                  <Switch 
                    checked={preferences.functional}
                    onCheckedChange={(checked) => setPreferences({...preferences, functional: checked})}
                    className="data-[state=checked]:bg-[#1E4DB7]"
                  />
                </div>

                {/* Advertising */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Advertising & Marketing</p>
                      <p className="text-xs text-slate-500">Personalized promotions</p>
                    </div>
                  </div>
                  <Switch 
                    checked={preferences.advertising}
                    onCheckedChange={(checked) => setPreferences({...preferences, advertising: checked})}
                    className="data-[state=checked]:bg-[#1E4DB7]"
                  />
                </div>

                {/* Location Note */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
                  <MapPin className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900 text-sm">Location & Device Data</p>
                    <p className="text-xs text-slate-600">GPS and device permissions are controlled through your device settings, not cookies.</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  data-testid="cookie-save-custom"
                  onClick={saveCustom}
                  className="flex-1 bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
                >
                  Save Preferences
                </Button>
                <Button 
                  onClick={acceptAll}
                  variant="outline"
                  className="flex-1 rounded-full border-slate-300"
                >
                  Accept All
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center mt-4">
                You can change your preferences anytime in Settings &gt; Privacy &gt; Cookie Preferences
              </p>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default CookieBanner;
